import React, { useEffect, useState } from 'react';
import { X, Bell } from 'lucide-react';

interface MacOSNotificationProps {
  id: string;
  title: string;
  description: string;
  onClose: (id: string) => void;
  duration?: number;
}

export const MacOSNotification: React.FC<MacOSNotificationProps> = ({
  id,
  title,
  description,
  onClose,
  duration = 5000
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 10);
    
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - (100 / (duration / 100));
        return newProgress <= 0 ? 0 : newProgress;
      });
    }, 100);

    // Auto-dismiss after duration
    const dismissTimer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(timer);
      clearTimeout(dismissTimer);
      clearInterval(progressInterval);
    };
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 400); // Match animation duration
  };

  return (
    <div
      className={`
        relative w-96 rounded-xl overflow-hidden
        transform transition-all duration-400 ease-out
        ${isVisible && !isExiting
          ? 'translate-x-0 opacity-100 scale-100'
          : 'translate-x-full opacity-0 scale-95'
        }
      `}
      style={{
        zIndex: 9999,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: `
          0 25px 50px -12px rgba(0, 0, 0, 0.25),
          0 0 0 1px rgba(255, 255, 255, 0.05),
          inset 0 1px 0 rgba(255, 255, 255, 0.1)
        `
      }}
    >
      {/* Close button - macOS notification style (gray circle with black X) */}
      <button
        onClick={handleClose}
        className="absolute top-3 left-3 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-200 hover:bg-gray-400 group"
        style={{
          background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
          border: '0.5px solid rgba(0, 0, 0, 0.2)',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2), inset 0 0.5px 0 rgba(255, 255, 255, 0.3)'
        }}
        aria-label="Close notification"
      >
        <X
          size={12}
          className="text-black/70 group-hover:text-black transition-colors duration-200"
          strokeWidth={2}
        />
      </button>

      {/* Notification icon */}
      <div className="absolute top-3 right-3">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
          }}
        >
          <Bell size={14} className="text-white" />
        </div>
      </div>

      {/* Content */}
      <div className="pt-8 pb-5 px-5 pl-10">
        <div className="font-semibold text-base mb-3 leading-tight text-gray-900">
          {title}
        </div>
        <div className="text-sm leading-relaxed text-gray-600">
          {description}
        </div>
      </div>

      {/* Progress bar */}
      <div 
        className="absolute bottom-0 left-0 h-1 transition-all duration-100 ease-linear"
        style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
          boxShadow: '0 0 8px rgba(59, 130, 246, 0.6)'
        }}
      />

      {/* Subtle inner glow */}
      <div 
        className="absolute inset-0 rounded-xl pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(255, 255, 255, 0.05) 100%)'
        }}
      />
    </div>
  );
};

// Notification Manager Component
interface NotificationData {
  id: string;
  title: string;
  description: string;
}

interface MacOSNotificationManagerProps {
  notifications: NotificationData[];
  onRemove: (id: string) => void;
}

export const MacOSNotificationManager: React.FC<MacOSNotificationManagerProps> = ({
  notifications,
  onRemove
}) => {
  return (
    <div 
      className="fixed top-0 right-0 pointer-events-none"
      style={{ zIndex: 9999 }}
    >
      <div className="flex flex-col gap-3 p-4 pointer-events-auto">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            className="transform transition-all duration-300 ease-out"
            style={{
              transform: `translateY(${index * 4}px)`,
              zIndex: 9999 - index,
              filter: index > 0 ? `brightness(${1 - index * 0.1})` : 'none'
            }}
          >
            <MacOSNotification
              id={notification.id}
              title={notification.title}
              description={notification.description}
              onClose={onRemove}
              duration={5000}
            />
          </div>
        ))}
      </div>
    </div>
  );
};